import { useDroppable } from '@dnd-kit/core'
import type { BloggerListItem, BloggerStage } from '@ayub/shared'
import { formatNumber } from '@ayub/shared'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { BloggerKanbanCard } from './BloggerKanbanCard'
import { BloggerKanbanColumnEmptyState } from './EmptyStates'
import { BLOGGER_STAGE_META, STAGE_KANBAN_LABELS } from './stageMeta'

interface BloggerKanbanColumnProps {
  stage: BloggerStage
  items: BloggerListItem[]
  total: number
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  hasMore: boolean
  onLoadMore: () => void
  onCardClick: (id: number) => void
}

/**
 * Kanban ustuni — `docs/04-ui-ux.md` "Kanban ustuni": kengligi desktop 300px / mobil 85vw,
 * sarlavha yopishqoq, ichida vertikal skroll, 50 tadan keyin "yana yuklash".
 */
export function BloggerKanbanColumn({
  stage,
  items,
  total,
  isLoading,
  isFetching,
  error,
  hasMore,
  onLoadMore,
  onCardClick,
}: BloggerKanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: stage })
  const meta = BLOGGER_STAGE_META[stage]

  return (
    <div className="flex w-[85vw] shrink-0 snap-start flex-col sm:w-[300px] sm:snap-align-none">
      <div className="sticky top-0 z-[1] flex items-center gap-2 rounded-t-card border border-b-0 border-app-border bg-app-card px-3 py-2">
        <span className={cn('size-2 shrink-0 rounded-full', meta.dotClass)} aria-hidden="true" />
        <h3 className="text-sm font-semibold text-app-text uppercase">
          {STAGE_KANBAN_LABELS[stage]}
        </h3>
        <span className="tabular ml-auto text-xs font-medium text-app-hint">
          {isLoading ? '…' : formatNumber(total)}
        </span>
      </div>

      <div
        ref={setNodeRef}
        // `aria-busy` — skrinrider uchun: ustun yuklanayotganda jim turmasin.
        aria-busy={isLoading}
        className={cn(
          'flex min-h-[200px] flex-1 flex-col gap-2 rounded-b-card border border-app-border bg-app-bg p-2 transition-colors duration-150 ease-out',
          isOver && 'bg-brand-subtle',
        )}
      >
        {isLoading && <ColumnSkeleton />}

        {!isLoading && error && items.length === 0 && <ColumnError error={error} />}

        {!isLoading && !error && items.length === 0 && <BloggerKanbanColumnEmptyState />}

        {items.map((blogger) => (
          <BloggerKanbanCard
            key={blogger.id}
            blogger={blogger}
            stage={stage}
            onCardClick={onCardClick}
          />
        ))}

        {!isLoading && items.length > 0 && hasMore && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetching}
            className="mt-1"
          >
            {isFetching ? 'Yuklanmoqda...' : 'Yana yuklash'}
          </Button>
        )}
      </div>
    </div>
  )
}

function ColumnSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-[88px]" />
      ))}
    </>
  )
}

function ColumnError({ error }: { error: Error }) {
  const isServerError = error instanceof ApiError
  return (
    <div className="rounded-card border border-urgency-overdue/30 bg-urgency-overdue/10 p-3 text-xs text-urgency-overdue-text">
      {isServerError ? error.message : "Aloqa yo'q. Qayta urinib ko'ring."}
    </div>
  )
}
